/**
 * `/app/account/set-up` — **confirm and set up the account a registration was created for**
 * (`USR-013`).
 *
 * ## Who is reading this
 *
 * Somebody an organizer typed into a walk-in form. They did not sign up, they may never have heard
 * of H2HSpark, and the first they knew of it was an email. Everything about this screen follows
 * from that: it says who held a place for them before it asks for anything, it asks for exactly
 * one thing, and it never implies that not doing it costs them something.
 *
 * Because that last point is easy to erode, it is worth stating plainly: **their place is held
 * whether or not they ever open this page** (`USR-013` AC-9). The seat, the ticket token and the
 * door token persist indefinitely and they can walk in and be scanned with no account at all. An
 * expired link costs a click, never a seat, and that is what makes the fourteen days affordable.
 *
 * ## Outside the shell, and in `MEMBER_APP_PUBLIC_PATHS`
 *
 * Declared in `App.tsx` alongside `/app/login` rather than inside `MemberLayout`, because its
 * reader has no session and having one is the point of the visit. `/app/account/verify-email` next
 * door is the opposite case and sits behind `ProtectedRoute` — that endpoint authenticates on the
 * caller's own access token, so there is no anonymous form there to build. Here the **mailed token
 * is the credential**.
 *
 * ## It does not submit on load
 *
 * The token is read out of `?token=` and inspected, which is a read; nothing is consumed until the
 * person presses the button. `verify-email-page.tsx` and `confirm-email-page.tsx` both carry this
 * rule and the reason is the same: a mail client or a link scanner that prefetches URLs would
 * spend a single-use credential before anybody saw the page.
 *
 * ## Any failure to inspect is a dead link
 *
 * A rejection — including a network failure — collapses to `used`, exactly as
 * `accept-invitation-page.tsx` does. The page never claims a link it could not check is fine, and
 * `used` is the state that offers nothing rather than the state that offers a replacement.
 */
import { AuthScreen } from '@h2hspark/auth-ui';
import { Button, Field, Input } from '@h2hspark/ui';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AuthBrandMark } from '../../member/auth-brand-mark';

import {
  completeAccountSetUp,
  inspectAccountSetUpLink,
  requestAnotherAccountSetUpLink,
} from './api';
import {
  ACCOUNT_SET_UP_COPY,
  ACCOUNT_SET_UP_DEAD_END_COPY,
  ACCOUNT_SET_UP_TOKEN_PARAM,
} from './constants';
import type { AccountSetUpInspection } from './types';

/** What an unresolvable, unreachable or unknown token becomes. Offers nothing; see the header. */
const DEAD_LINK: AccountSetUpInspection = {
  state: 'used',
  emailAddress: null,
  mayRequestAnother: false,
};

export function MemberAccountSetUpPage(): React.ReactElement {
  const [params] = useSearchParams();
  const token = params.get(ACCOUNT_SET_UP_TOKEN_PARAM) ?? '';

  const [inspection, setInspection] = React.useState<AccountSetUpInspection | null>(null);
  const [password, setPassword] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [failure, setFailure] = React.useState<string | null>(null);
  const [replacement, setReplacement] = React.useState<string | null>(null);
  const [requesting, setRequesting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (token === '') {
      // No round trip for a link with no token in it. There is nothing to ask about.
      setInspection(DEAD_LINK);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    void inspectAccountSetUpLink(token, controller.signal)
      .then((resolved) => {
        if (!cancelled) setInspection(resolved);
      })
      .catch(() => {
        if (!cancelled) setInspection(DEAD_LINK);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  if (done) {
    return (
      <SetUpShell>
        <p role="status" className="text-sm">
          {ACCOUNT_SET_UP_COPY.done}
        </p>
        <Button asChild>
          <Link to="/app/login">{ACCOUNT_SET_UP_COPY.signIn}</Link>
        </Button>
      </SetUpShell>
    );
  }

  if (inspection === null) {
    return (
      <SetUpShell>
        <p role="status" className="text-sm text-muted-foreground">
          {ACCOUNT_SET_UP_COPY.checking}
        </p>
      </SetUpShell>
    );
  }

  if (inspection.state !== 'usable') {
    // **No password field is rendered here at all.** A dead link that still showed the form would
    // invite somebody to type a password into a page that cannot use it, and the refusal would
    // arrive after they had chosen one.
    return (
      <SetUpShell>
        <p role="alert" className="text-sm">
          {ACCOUNT_SET_UP_DEAD_END_COPY[inspection.state]}
        </p>
        {inspection.mayRequestAnother ? (
          <>
            {/* `USR-013` AC-7. It spends the expired token the reader is already holding — there
                is deliberately no address field on this page, because a public resend keyed on an
                address would be an enumeration oracle on exactly this population. */}
            <Button
              type="button"
              disabled={requesting}
              onClick={() => {
                setRequesting(true);
                void requestAnotherAccountSetUpLink(token)
                  .then((answer) => setReplacement(answer.notice))
                  .catch(() => setReplacement(null))
                  .finally(() => setRequesting(false));
              }}
            >
              {requesting
                ? ACCOUNT_SET_UP_COPY.requestingAnother
                : ACCOUNT_SET_UP_COPY.requestAnother}
            </Button>
            {replacement === null ? null : (
              <p role="status" className="text-sm text-muted-foreground">
                {replacement}
              </p>
            )}
          </>
        ) : (
          <Button asChild variant="outline">
            <Link to="/app/login">{ACCOUNT_SET_UP_COPY.signIn}</Link>
          </Button>
        )}
      </SetUpShell>
    );
  }

  return (
    <SetUpShell>
      <p className="text-sm">{ACCOUNT_SET_UP_COPY.intro}</p>
      {inspection.emailAddress === null ? null : (
        <p className="text-sm text-muted-foreground">
          {ACCOUNT_SET_UP_COPY.emailLabel}: {inspection.emailAddress}
        </p>
      )}
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          setFailure(null);
          setSubmitting(true);
          void completeAccountSetUp({
            token,
            password,
            ...(displayName.trim() === '' ? {} : { displayName: displayName.trim() }),
          })
            .then(() => setDone(true))
            .catch((error: unknown) => {
              // The server's sentence, verbatim. Every refusal on that route is deliberately the
              // same one — telling a holder that a link is real but belongs to somebody else is a
              // fact they have no business confirming.
              setFailure(error instanceof Error ? error.message : ACCOUNT_SET_UP_COPY.title);
            })
            .finally(() => setSubmitting(false));
        }}
      >
        <Field
          label={ACCOUNT_SET_UP_COPY.displayNameLabel}
          htmlFor="account-set-up-name"
          hint={ACCOUNT_SET_UP_COPY.displayNameHint}
        >
          <Input
            id="account-set-up-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>
        <Field label={ACCOUNT_SET_UP_COPY.passwordLabel} htmlFor="account-set-up-password">
          <Input
            id="account-set-up-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        {failure === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
        <Button type="submit" disabled={submitting || password === ''}>
          {submitting ? ACCOUNT_SET_UP_COPY.submitting : ACCOUNT_SET_UP_COPY.submit}
        </Button>
      </form>
    </SetUpShell>
  );
}

/**
 * The frame, shared by all four states so the page does not change shape as it resolves.
 *
 * Built from the member app's own out-of-shell screens (`login-page.tsx`, `recovery-page.tsx`)
 * rather than from the design handoff, because **the handoff has no account-claim screen** — the
 * wireframe archive covers the member app and the marketing site and never draws this flow. That
 * is the same situation `ORG-010`'s `accept-invitation-page.tsx` records for the organizer
 * console, and this is its resolution: reuse the app's established out-of-shell card rather than
 * invent a layout. Recorded in `docs/design/handoff-corrections.md`.
 *
 * On 2026-09-12 that reuse became literal: `AuthScreen` *is* the app's out-of-shell screen, so
 * this frame is now the same component `login-page.tsx` and `recovery-page.tsx` render rather
 * than a copy of what they used to render. The copy had already drifted — it was the only one of
 * the four with a heading inside the card.
 */
function SetUpShell({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <AuthScreen chrome="brand" mark={<AuthBrandMark />} title={ACCOUNT_SET_UP_COPY.title}>
      <div className="space-y-5">{children}</div>
    </AuthScreen>
  );
}
